/**
 * CaseNoteClientChooser - the searchable list of clients a case note can
 * be filed on. The "Log as case note" dialog shows it when no client is
 * named, and the Email helper's Care Management face shows the same list
 * behind its "For" line, so a care manager names a client one way on
 * both.
 *
 * Where the list comes from depends on what the viewer may read. An
 * author who may open the client roster (canViewAnyClientRoster: every
 * client, or the viewer's engagement lane, as GET /v1/clients answers)
 * lists every client that read returns, a line seat's own clients first,
 * because coverage and emergencies need any of them, a client with no care
 * manager included. Without the roster, an office, exec or supervising
 * seat (view_workspace) lists the workspace caseload, and a line seat her
 * own caseload from the home read. Each lists the clients she can open. A demo-only author (board_observer, 0156) lists demo
 * clients only; the server fences that write regardless.
 *
 * Mounting the chooser is what reads the list, none before.
 */
import { type ReactNode, useMemo, useState } from 'react';
import { useTenant } from '@/lib/auth';
import {
  canAuthorClinicalNotes,
  canReadEveryClient,
  canViewAnyClientRoster,
  canViewWorkspace,
  canWriteClinicalNotes,
  canWriteDemoClinical,
} from '@/lib/capabilities';
import { useCaseload } from '@/lib/workspace';
import { useClientsList } from '@/lib/clientsApi';
import { useCmHome } from '@/lib/cmHomeApi';
import { practiceTodayIso } from '@/lib/practiceDate';
import { Button } from '@/components/primitives/Button';
import { SearchField } from '@/components/primitives/SearchField';

interface ChooserClient {
  id: string;
  display_name: string;
  is_demo: boolean;
}

export interface CaseNoteClientChooserProps {
  onChoose: (clientId: string, displayName: string) => void;
  /** Prefix for every test id inside; the dialog keeps `log-as-note`. */
  testIdPrefix?: string;
  /** The search box's accessible name; visible when `labelVisible`. */
  label?: string;
  labelVisible?: boolean;
  /** What to do when the list fails to load, in the words of the place
   *  the chooser sits (the dialog closes; the page's chooser reopens). */
  retryHint?: string;
}

const LABEL_CLS = 'block text-title-sm font-semibold text-sage-800';

export function CaseNoteClientChooser({
  onChoose,
  testIdPrefix = 'log-as-note',
  label = 'Search clients',
  labelVisible = false,
  retryHint = 'Close and try again.',
}: CaseNoteClientChooserProps): ReactNode {
  const { whoami, tenantSlug } = useTenant();
  const [search, setSearch] = useState('');

  // Tiers (see the file header). A seat that can never author a note
  // reads nothing and lists nothing.
  const canAuthorNotes = canAuthorClinicalNotes(whoami);
  const demoOnly = canWriteDemoClinical(whoami) && !canWriteClinicalNotes(whoami);
  const usesEveryClient = canAuthorNotes && canViewAnyClientRoster(whoami);
  const usesWorkspaceList = canAuthorNotes && canViewWorkspace(whoami) && !usesEveryClient;
  // A line seat's own caseload: the list itself when it has no roster, and
  // the clients that come first when it does.
  const usesOwnCaseload = canAuthorNotes && !canViewWorkspace(whoami) && !canReadEveryClient(whoami);
  const todayIso = useMemo(() => practiceTodayIso(), []);

  const workspace = useCaseload(tenantSlug, { enabled: usesWorkspaceList });
  const roster = useClientsList(tenantSlug, { enabled: usesEveryClient });
  const home = useCmHome(tenantSlug, todayIso, { enabled: usesOwnCaseload });
  const isLoading = usesWorkspaceList ? workspace.isLoading : usesEveryClient ? roster.isLoading : home.isLoading;
  const isError = usesWorkspaceList ? workspace.isError : usesEveryClient ? roster.isError : home.isError;
  const ownIds = useMemo(() => new Set((home.home?.clients ?? []).map((c) => c.id)), [home.home]);

  const clients = useMemo<ChooserClient[]>(() => {
    const seen = new Map<string, ChooserClient>();
    if (usesWorkspaceList) {
      for (const cm of workspace.careManagers) {
        for (const c of cm.clients) {
          if (!seen.has(c.id))
            seen.set(c.id, { id: c.id, display_name: c.display_name, is_demo: c.is_demo });
        }
      }
    } else if (usesEveryClient) {
      // Her own clients, then every other client the roster read returns.
      for (const c of home.home?.clients ?? []) {
        if (!seen.has(c.id)) seen.set(c.id, { id: c.id, display_name: c.display_name, is_demo: false });
      }
      for (const c of roster.clients) {
        if (!seen.has(c.id))
          seen.set(c.id, { id: c.id, display_name: c.display_name, is_demo: c.is_demo });
      }
    } else {
      for (const c of home.home?.clients ?? []) {
        if (!seen.has(c.id))
          seen.set(c.id, { id: c.id, display_name: c.display_name, is_demo: false });
      }
    }
    let rows = [...seen.values()];
    // Demo-only author (board_observer): the list holds ONLY demo clients.
    if (demoOnly) rows = rows.filter((r) => r.is_demo);
    // By name, the viewer's own clients first.
    rows.sort((a, b) => Number(ownIds.has(b.id)) - Number(ownIds.has(a.id)) || a.display_name.localeCompare(b.display_name));
    const q = search.trim().toLowerCase();
    return q === '' ? rows : rows.filter((r) => r.display_name.toLowerCase().includes(q));
  }, [usesWorkspaceList, workspace.careManagers, usesEveryClient, roster.clients, home.home, search, demoOnly, ownIds]);

  const searchId = `${testIdPrefix}-search`;

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-chooser`}>
      <div>
        <label htmlFor={searchId} className={labelVisible ? LABEL_CLS : 'sr-only'}>
          {label}
        </label>
        <SearchField
          id={searchId}
          testId={searchId}
          placeholder="Search clients"
          value={search}
          onChange={setSearch}
          className={labelVisible ? 'mt-1.5' : undefined}
        />
      </div>
      {isLoading && (
        <p className="text-sm text-ink-soft" data-testid={`${testIdPrefix}-loading`}>
          Loading clients…
        </p>
      )}
      {isError && (
        <p className="text-sm text-pepper" role="alert" data-testid={`${testIdPrefix}-error`}>
          Could not load the client list. {retryHint}
        </p>
      )}
      {!isLoading && !isError && clients.length === 0 && (
        <p className="text-sm text-ink-soft" data-testid={`${testIdPrefix}-empty`}>
          {search.trim() === '' ? 'No matching clients.' : 'Try another spelling.'}
        </p>
      )}
      {clients.length > 0 && (
        <ul className="max-h-64 space-y-1 overflow-y-auto" data-testid={`${testIdPrefix}-client-list`}>
          {clients.map((c) => (
            <li key={c.id}>
              <Button
                variant="ghost"
                size="sm"
                className="w-full !justify-start"
                data-testid={`${testIdPrefix}-client-${c.id}`}
                onClick={() => onChoose(c.id, c.display_name)}
              >
                {c.display_name}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
